import { FC, useState } from 'react';
import { IonButton } from '@ionic/react';
import { isPlatform } from '../../utils/isPlatform';
import { useDropzone } from 'react-dropzone';
import { DuoPrescriptionBottleMedicalIcon } from '../../theme/Icons';
import { IMedication } from '../../caremap/ICareMap';
import { Ocr, TextDetections } from '@capacitor-community/image-to-text';
import { Spinner } from '../../components/Spinner';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { AlertPresenter } from '../../AlertBinder';
import { MixPanelEventEmitter } from '../../metrics/MixPanelEventEmitter';
import { MedicationService } from './MedicationService';

export type IAddMedicationButtonProps = {
    useIcon?: boolean;
    forceManually?: boolean;
    onAdd: (initialValues?: IMedication, usedPhoto?: boolean) => void;
};

export const AddMedicationButton: FC<IAddMedicationButtonProps> = ({ useIcon, forceManually, onAdd }) => {
    const isDesktop = isPlatform('desktop');
    const label = forceManually ? 'Add Manually' : 'Add Medication';

    const alert = useInstance(AlertPresenter);
    const mixpanel = useInstance(MixPanelEventEmitter);
    const medicationService = useInstance(MedicationService);

    const [processing, setProcessing] = useState<boolean>(false);

    const {getInputProps, open} = useDropzone({
        noClick: true,
        onDropAccepted: async (files: File[]) => {
            setProcessing(true);

            const ocrResult: String[] = [];
            const reader = new FileReader();
            reader.onloadend = async () => {
                const base64 = reader.result as string;
                try {
                    const data: TextDetections = await Ocr.detectText({ base64: base64 });
                    for (const detection of data.textDetections) {
                        ocrResult.push(detection.text);
                    }

                    const parsedResponse = await medicationService.parseMedicationText({ content: ocrResult.join('') });

                    const generated = (attr: keyof IMedication) => parsedResponse[attr] ? true : false;
                    mixpanel.track('Medication Photo Processed', () => ({
                        'Name Generated': generated('name'),
                        'Instructions Generated': generated('instructions'),
                        'Dose Generated': generated('dose'),
                        'RX # Generated': generated('rxNumber'),
                        'Refills Generated': generated('refills'),
                        'Expiration Date Generated': generated('expiration'),
                    }));

                    setProcessing(false);
                    onAdd(parsedResponse, true);
                } catch (e) {
                    alert.showAndLogError(e);
                    setProcessing(false);
                }
            };

            try {
                reader.readAsDataURL(files[0]);
            } catch (e) {
                alert.showAndLogError(e);
                setProcessing(false);
            }
        }
    });

    const onClick = async () => {
        const desktopOrManual = isDesktop || forceManually;
        desktopOrManual ? onAdd() : open();
        mixpanel.track('Add Medication Initiated', () => ({
            'Type': desktopOrManual ? 'manual' : 'photo'
        }));
    };

    return (
        <div style={{position: 'relative'}}>
            {processing ? (
                <div style={{paddingRight: 16, marginBottom: -6}}>
                    <Spinner size="small" />
                </div>
            ) : (
                <IonButton
                    fill="clear"
                    color="primary"
                    onClick={onClick}
                    data-id="add-medication-button"
                >
                    {useIcon ? <DuoPrescriptionBottleMedicalIcon /> : '+'}&nbsp;{label}
                </IonButton>
            )}
            <input {...getInputProps()} style={{display: 'none'}} />
        </div>
    );
};
